import { defineStore } from 'pinia';

import type { Transaction, TransactionItem } from '@/shared/interfaces/transaction';
import { Currency } from '@/shared/interfaces/balance';

export const useTransactionStore = defineStore('transaction', {
	state: () => ({
		additionalInfo: <string | number>'',
		additionalInfoMore: <string | number>'',
		items: [] as TransactionItem[],
		type: <string | undefined>'',
		subtype: <string | undefined>'',
		title: <string | undefined>'',
		subtitle: <string | undefined>'',
		totalDescription: <string>'',
		totalPriceString: <string>'',
		showTotalPriceString: <boolean | undefined>true,
		totalPriceNumber: <number>0,
		totalPriceWithoutFee: <number>0,
		totalPricePerCurrency: <
			{
				currencyId: any;
				currencyPrefix: any;
				currencyName: any;
				total: number;
			}[]
		>[],
	}),
	getters: {
		getTotalPrice(state) {
			return state.totalPriceNumber;
		},
		totalItemsCount(state) {
			return state.items.reduce((count, item) => count + item.quantity, 0);
		},
	},
	actions: {
		calculateTotalsPerProductCurrency() {
			const groups = this.items.reduce(
				(acc, obj) => {
					const key = obj.additionalInfo?.product?.currency?.name ?? '';
					const curGroup = acc[key] ?? [];
					return { ...acc, [key]: [...curGroup, obj] };
				},
				{} as { [key: string]: TransactionItem[] },
			);

			const totals = Object.keys(groups).map((key: string) => {
				const currency = (groups[key][0].additionalInfo?.product?.currency ?? {}) as Currency;
				return {
					currencyId: currency.id,
					currencyPrefix: currency && currency.prefix ? currency.prefix : '',
					currencyName: currency && currency.name ? currency.name : '',
					exchangeRate: currency.exchange_rate || 1,
					total: groups[key].reduce((a, b: TransactionItem) => {
						return a + b.itemPrice;
					}, 0),
				};
			});

			this.totalPricePerCurrency = totals.map((total) => ({
				...total,
				total: parseFloat(total.total.toFixed(2)),
			}));

			return this.totalPricePerCurrency.reduce((acc: string, obj: { currencyPrefix: string; currencyName: string; total: number }, index: number) => {
				const roundedTotal = obj.total.toFixed(2);
				const separator = index === 0 ? '' : ' + ';
				return `${acc}${separator}${obj.currencyPrefix} ${roundedTotal}`;
			}, '' as string);
		},
		calculateTotalInEur() {
			const groups = this.items.reduce(
				(acc, obj) => {
					const key = obj.additionalInfo?.product?.currency?.name ?? obj.currency ?? '';
					const curGroup = acc[key] ?? [];
					return { ...acc, [key]: [...curGroup, obj] };
				},
				{} as { [key: string]: TransactionItem[] },
			);

			const totals = Object.keys(groups).map((key: string) => {
				const item = groups[key][0];
				const currency = (item.additionalInfo?.product?.currency ?? {}) as Currency;
				const exchangeRate = currency.exchange_rate || 1;
				const total = groups[key].reduce((a, b: TransactionItem) => {
					return a + b.itemPrice;
				}, 0);
				return { currencyName: currency.name || '', totalInEur: total * exchangeRate };
			});

			const hasNonEur = totals.some((t) => t.currencyName !== 'EUR');
			if (!hasNonEur) return '';

			const totalEur = totals.reduce((a, t) => a + t.totalInEur, 0);
			return `€${totalEur.toFixed(2)}`;
		},
		getTotalPricePerCurrency() {
			return this.totalPricePerCurrency;
		},
		getItemsAndTotal() {
			return {
				additionalInfo: this.additionalInfo,
				additionalInfoMore: this.additionalInfoMore,
				items: this.items,
				type: this.type,
				subtype: this.subtype,
				title: this.title,
				subtitle: this.subtitle,
				totalDescription: this.totalDescription,
				totalPriceString: this.totalPriceString,
				showTotalPriceString: this.showTotalPriceString,
				totalPriceNumber: this.totalPriceNumber,
				totalPriceWithoutFee: this.totalPriceWithoutFee,
			};
		},
		setItemsAndTotal(transaction: Transaction) {
			this.additionalInfo = transaction.additionalInfo;
			this.additionalInfoMore = transaction.additionalInfoMore;
			this.type = transaction.type ? transaction.type : undefined;
			this.subtype = transaction.subtype ? transaction.subtype : undefined;
			this.title = transaction.title ? transaction.title : undefined;
			this.subtitle = transaction.subtitle ? transaction.subtitle : undefined;
			this.items = transaction.items;
			this.totalDescription = transaction.totalDescription;
			this.totalPriceString = transaction.totalPriceString;
			this.showTotalPriceString = transaction.showTotalPriceString;
			this.totalPriceNumber = transaction.totalPriceNumber;
			this.totalPriceWithoutFee = transaction.totalPriceWithoutFee;
		},
		setTotalPriceString(totalPriceString: string) {
			this.totalPriceString = totalPriceString;
		},
		setAdministrativeFee(administrativeFee: number, description: string) {
			const existingFee = this.items.find((item) => item.description === description);
			if (!existingFee) {
				this.items.push({
					additionalInfo: {
						id: 'administrative_fee',
						product: {
							currency: {
								name: 'EUR',
								prefix: '€',
							},
						},
					},
					description: description,
					itemPrice: administrativeFee,
					quantity: 1,
					currency: '€',
				});
			}
		},
		clearAdministrativeFee() {
			const itemsWithoutFee = this.items.filter((item) => item.additionalInfo?.id !== 'administrative_fee');
			this.items.splice(0, this.items.length, ...itemsWithoutFee);
		},
		setRfidCost(rfIdCost: number, description: string) {
			const existingFee = this.items.find((item) => item.description === description);
			if (!existingFee) {
				this.items.push({
					description: description,
					itemPrice: rfIdCost,
					quantity: 1,
					currency: '€',
				});
			}
		},
		clear() {
			this.$reset();
		},
	},
	persist: true,
});
